import { BookmarkIcon, ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DropdownMenu, DropdownMenuIconButton, Text } from '@/shared/ui';

type Props = {
  /** タイトル。welcome 画面では undefined */
  title?: string;
  /** 渡さない場合は + アイコンを表示しない（welcome 画面では非表示） */
  onNewChat?: () => void;
  /** 渡さない場合は 🕐 アイコンを表示しない（履歴一覧表示中は非表示） */
  onShowHistory?: () => void;
  /** 渡さない場合はメモ作成アイコンを表示しない（スレッド選択中のみ表示） */
  onCreateMemo?: () => void;
  /** 渡さない場合は 3 点メニューを表示しない（スレッド選択中のみ表示） */
  menuItems?: ComponentProps<typeof DropdownMenu>['menuItems'];
};

/**
 * 事業詳細チャットヘッダーの 2 段目。タイトル + アクションアイコンを表示する。
 * 1 段目（× 閉じる）は `SideMenuHeader` 側に集約しているため、ここでは扱わない。
 */
export const ProjectDetailAIChatActionBar = ({
  title,
  onNewChat,
  onShowHistory,
  onCreateMemo,
  menuItems,
}: Props) => {
  const { t } = useTranslation();

  const newChatLabel = t('ai.chat.new-chat-button');
  const historyLabel = t('ai.chat.history-button');
  const createMemoLabel = t('ai.chat.create-memo-from-thread');

  // 高さはメインエリアヘッダー（PageLayoutFrame の h-[48px]）と揃えるため 48px に固定する
  return (
    <div className="flex h-[48px] shrink-0 items-center justify-between gap-2 border-b bg-white px-4">
      <div className="min-w-0 flex-1">
        {title && (
          <Text variant="subhead" as="h2" truncate>
            {title}
          </Text>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {onNewChat && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onNewChat}
            aria-label={newChatLabel}
            tooltip={{ content: newChatLabel }}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        )}
        {onCreateMemo && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onCreateMemo}
            aria-label={createMemoLabel}
            tooltip={{ content: createMemoLabel }}
          >
            <BookmarkIcon className="h-4 w-4" />
          </Button>
        )}
        {onShowHistory && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onShowHistory}
            aria-label={historyLabel}
            tooltip={{ content: historyLabel }}
          >
            <ClockIcon className="h-4 w-4" />
          </Button>
        )}
        {menuItems && (
          <DropdownMenu menuItems={menuItems}>
            <DropdownMenuIconButton aria-label={t('ai.chat.menu-button')} />
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
